import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Logo } from "@/components/intel/Logo";
import { Ticker } from "@/components/intel/Ticker";
import { JoinList } from "@/components/intel/JoinList";
import { SiteChat } from "@/components/marketing/SiteChat";
import {
  ArrowRight, Search, Network, Shield, Brain, Globe, FileText, Eye,
  Zap, Lock, Fingerprint, Activity, Target, Database, Radio, ChevronRight, Menu, X,
  Radar, Camera, GitCompare, Gauge, Building2, LayoutGrid, TrendingUp, ShieldCheck, Bot,
} from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "CARIO — Open-source intelligence research, in one workspace" },
      { name: "description", content: "Search a name, email, domain, phone or wallet across hundreds of public sources. Get a sourced case file you can read, question and export." },
      { property: "og:title", content: "CARIO — OSINT research in one workspace" },
      { property: "og:description", content: "Search public records, breaches, registries and news from one place. Every claim keeps its source." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Landing,
});

const CAPABILITIES = [
  { icon: Search,     k: "01", t: "Start from anything",      d: "A name, email, domain, IP, phone, wallet, photo or document. You don't have to know which kind it is — we work that out." },
  { icon: Network,    k: "02", t: "See the connections",      d: "Directors, shared addresses, linked accounts and infrastructure laid out on a graph you can drag around and add to." },
  { icon: Brain,      k: "03", t: "An AI that checks itself", d: "It reads the raw results, points out where two sources disagree, and tells you what it still isn't sure about." },
  { icon: Shield,     k: "04", t: "Risk you can argue with",  d: "Scores are built from what was actually found — a sanctions hit, a conviction, a leaked password — and each one is listed." },
  { icon: Globe,      k: "05", t: "Domains and infrastructure", d: "WHOIS, DNS history, certificates, subdomains, hosting and location, including how they looked years ago." },
  { icon: FileText,   k: "06", t: "Reports you can hand over", d: "Export a PDF with the sources, timestamps and hashes intact, plus whatever notes you added along the way." },
];

const MODULES = [
  { icon: Bot,         t: "Investigation copilot",   d: "Reads the case with you, suggests where to look next, and says when a lead has gone nowhere. You can see how it got there." },
  { icon: Fingerprint, t: "Identity resolution",     d: "Works out which usernames, emails, companies and wallets belong to the same person, and shows what made it think so." },
  { icon: GitCompare,  t: "Contradictions",          d: "Checks what someone claims against the registries. Still \u201cCEO\u201d on LinkedIn, resigned in 2023 on file? You'll see it." },
  { icon: Gauge,       t: "Exposure score",          d: "How much of someone is public, scored 300\u2013850 like a credit rating, broken down by category with fixes for each." },
  { icon: Building2,   t: "Company dossiers",        d: "Owners, subsidiaries, directors, lawsuits, patents, sanctions and hiring, pulled together from registries in one go." },
  { icon: Radar,       t: "Attack surface review",   d: "What an attacker could see from outside: exposed services, leaked credentials, forgotten subdomains. Nothing is touched or probed." },
  { icon: TrendingUp,  t: "Forecasts, with caveats", d: "Rough odds on things like insolvency or a project walking away with the money, always shown next to the evidence against." },
  { icon: Camera,      t: "Photo analysis",          d: "Upload a picture: read the text and logos in it, pull GPS from the file, and get a shortlist of where it was probably taken." },
  { icon: Radio,       t: "Keep watching",           d: "Put an email, company, domain or wallet on watch and get told when something changes — a new breach, a new role, a new case." },
  { icon: LayoutGrid,  t: "Canvas",                  d: "A blank board where you can drag people, companies, screenshots and notes around until the picture makes sense." },
  { icon: ShieldCheck, t: "Evidence integrity",      d: "Everything collected is hashed and timestamped, so you can show later that nothing was quietly edited." },
  { icon: Database,    t: "Your vault",              d: "Every search you run stays in your account. Reopen it months later, ask the AI about it, add notes, tasks and files." },
];

const STAT = [
  { v: "600+",   l: "Public sources read" },
  { v: "Every claim", l: "Linked to its source" },
  { v: "1 day",  l: "Free trial, card required" },
  { v: "Yours",  l: "Cases stay in your vault" },
];

const SOURCES = [
  "OpenCorporates", "Companies House", "EDGAR", "GLEIF", "OFAC", "EU Sanctions",
  "Etherscan", "Chainalysis", "Shodan", "Censys", "SecurityTrails", "GreyNoise",
  "HaveIBeenPwned", "Archive.org", "GDELT", "GitHub", "Pastebin", "WHOIS",
];

function Landing() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-panel-border bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6">
          <Logo />
          <nav className="ml-10 hidden gap-6 md:flex">
            <a href="#capabilities" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Platform</a>
            <a href="#modules" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Modules</a>
            <a href="#sources" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sources</a>
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
            <Link to="/guide" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Docs</Link>
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <Link to="/pricing" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              Pricing
            </Link>
            <Link to="/login" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              Sign in
            </Link>
            <Link to="/signup" className="group inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs sm:text-sm font-medium text-primary-foreground hover:opacity-90 ring-glow whitespace-nowrap">
              Create
              <span className="hidden sm:inline">&nbsp;account</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(v => !v)}
              className="md:hidden inline-flex items-center justify-center rounded-md border border-panel-border bg-panel/40 p-1.5 text-muted-foreground hover:text-foreground"
            >
              {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <div className="md:hidden border-t border-panel-border bg-background/95 backdrop-blur-xl">
            <nav className="mx-auto max-w-7xl px-4 py-3 flex flex-col gap-1">
              {[
                { n: "Platform", h: "#capabilities" },
                { n: "Modules", h: "#modules" },
                { n: "Sources", h: "#sources" },
              ].map(({ n, h }) => (
                <a key={n} href={h} onClick={() => setMobileNavOpen(false)} className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-panel/40 hover:text-foreground">{n}</a>
              ))}
              <div className="my-1 h-px bg-panel-border" />
              <Link to="/pricing" onClick={() => setMobileNavOpen(false)} className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-panel/40 hover:text-foreground">Pricing</Link>
              <Link to="/blog" onClick={() => setMobileNavOpen(false)} className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-panel/40 hover:text-foreground">Blog</Link>
              <Link to="/guide" onClick={() => setMobileNavOpen(false)} className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-panel/40 hover:text-foreground">Docs</Link>
              <Link to="/login" onClick={() => setMobileNavOpen(false)} className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-panel/40 hover:text-foreground">Sign in</Link>
              <Link to="/signup" onClick={() => setMobileNavOpen(false)} className="rounded-md px-2 py-2 text-sm text-cyan hover:bg-panel/40">Create account</Link>
            </nav>
          </div>
        )}
      </header>

      <Ticker />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-panel-border bg-panel/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan pulse-dot" />
                <span className="truncate max-w-[14rem] sm:max-w-none">Public records · breaches · registries · news</span>
              </div>
              <h1 className="mt-6 font-display text-4xl leading-[1.05] sm:text-6xl lg:text-7xl">
                Find out who you're<br />
                actually <span className="text-glow text-cyan">dealing with</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-muted-foreground">
                Type a name, email, domain, phone number or wallet. CARIO checks hundreds of public
                sources at once — company registries, sanctions lists, breach data, court records,
                news — and hands back one case file where every line says where it came from.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/signup" className="group inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 ring-glow">
                  Start 1-day free trial
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <a href="#capabilities" className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-5 py-3 text-sm font-medium hover:bg-panel">
                  <Eye className="h-4 w-4" /> See what it checks
                </a>
              </div>
              <QuickScan />
              <div className="mt-6 max-w-xl">
                <JoinList source="hero" variant="compact" cta="Join list" />
                <p className="mt-2 text-xs text-muted-foreground">
                  Occasional product updates. No spam, unsubscribe in one click.
                </p>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4 max-w-2xl">
                {STAT.map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-2xl font-semibold text-glow">{s.v}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero terminal mock */}
            <div className="lg:col-span-5">
              <div className="glass-strong relative rounded-xl p-3 ring-glow scanline overflow-hidden">
                <div className="flex items-center gap-2 px-2 pb-2 border-b border-panel-border">
                  <div className="h-2.5 w-2.5 rounded-full bg-danger" />
                  <div className="h-2.5 w-2.5 rounded-full bg-amber" />
                  <div className="h-2.5 w-2.5 rounded-full bg-success" />
                  <div className="ml-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    cario — live search
                  </div>
                  <div className="ml-auto font-mono text-[10px] text-success">● LIVE</div>
                </div>
                <div className="font-mono text-[12px] leading-relaxed p-3 space-y-1.5">
                  <div><span className="text-cyan">analyst@cario</span><span className="text-muted-foreground">:~$ </span><span>intel resolve --target </span><span className="text-muted-foreground">&lt;your target&gt;</span></div>
                  <div className="text-muted-foreground">[•] waiting for a target — nothing here is simulated</div>
                  <div className="text-muted-foreground">[•] registries · sanctions · breaches · domains · wallets · news</div>
                  <div className="text-muted-foreground">[•] every result keeps its link, source and timestamp</div>
                  <div className="pt-2 flex items-center gap-2">
                    <span className="text-cyan">analyst@cario</span><span className="text-muted-foreground">:~$</span>
                    <span className="inline-block h-3.5 w-2 bg-cyan/80 animate-pulse" />
                  </div>
                </div>
                <div className="border-t border-panel-border p-3 grid grid-cols-3 gap-2 text-center">
                  <Stat label="ENTITIES" v="—" />
                  <Stat label="RISK" v="—" tone="amber" />
                  <Stat label="CONFIDENCE" v="—" tone="cyan" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Source rail */}
      <section id="sources" className="border-y border-panel-border bg-panel/30 py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Sources we read</div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            {SOURCES.map(s => (
              <span key={s} className="text-sm text-muted-foreground">{s}</span>
            ))}
          </div>
          <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              We add new sources most weeks. Want to hear when we do?
            </p>
            <JoinList source="sources-rail" variant="compact" cta="Notify me" />
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-cyan">What it does</div>
              <h2 className="mt-2 font-display text-3xl sm:text-5xl">The boring parts of a<br/> background check, done fast.</h2>
            </div>
            <p className="max-w-md text-muted-foreground">
              The work most people do by hand across twenty browser tabs: registry lookups, breach checks,
              sanctions screening, reading old news. CARIO does it in one pass and shows its working.
            </p>
          </div>

          <div className="mt-12 grid gap-px overflow-hidden rounded-xl border border-panel-border bg-panel-border md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map(({ icon: I, k, t, d }) => (
              <div key={k} className="group relative bg-background p-6 transition-colors hover:bg-panel/60">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] tracking-widest text-muted-foreground">CAP_{k}</div>
                  <I className="h-4 w-4 text-cyan" />
                </div>
                <h3 className="mt-6 font-display text-xl">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
                <div className="mt-6 flex items-center gap-1.5 text-xs text-cyan opacity-0 transition-opacity group-hover:opacity-100">
                  Learn more <ChevronRight className="h-3 w-3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="relative border-t border-panel-border py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-xs font-medium uppercase tracking-widest text-cyan">Inside a case</div>
          <h2 className="mt-2 font-display text-3xl sm:text-5xl">Twelve tools, one case file.</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            They all work on the same record, so a note you write, an entity you add and a document you
            upload stay together. Nothing lives in a separate silo you have to remember to check.
          </p>
          <div className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {MODULES.map(({ icon: I, t, d }) => (
              <div key={t} className="glass rounded-xl p-5">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-9 w-9 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
                    <I className="h-4 w-4 text-cyan" />
                  </div>
                  <h3 className="font-display text-base">{t}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/guide" className="inline-flex items-center gap-2 text-sm text-cyan hover:underline">
              Read the user guide <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Architecture strip */}
      <section className="border-y border-panel-border bg-panel/20 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <div className="text-xs font-medium uppercase tracking-widest text-cyan">How it works</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">More than a search box.</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">
              One search kicks off hundreds of parallel lookups. As answers come back they're matched to
              the same person or company, conflicts are flagged, and the AI writes a summary you can
              check line by line against the sources.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 max-w-lg">
              {[
                { i: Zap, t: "Queue orchestrator" },
                { i: Database, t: "Entity store + vector index" },
                { i: Brain, t: "AI correlation layer" },
                { i: Lock, t: "Audit + chain-of-custody" },
                { i: Radio, t: "Streaming telemetry (WS)" },
                { i: Fingerprint, t: "Identity resolution engine" },
              ].map(({ i: I, t }) => (
                <div key={t} className="flex min-w-0 items-center gap-2.5 rounded-md border border-panel-border bg-background p-3">
                  <I className="h-4 w-4 text-cyan" />
                  <span className="min-w-0 text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass rounded-xl p-5">
            <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">What happens after you hit search</div>
            <div className="mt-4 space-y-2.5">
              {[
                ["1", "We read what you typed and figure out what it is"],
                ["2", "Hundreds of public sources get queried at once"],
                ["3", "Registries, sanctions lists, breaches, domains, news"],
                ["4", "Duplicates are merged into one person or company"],
                ["5", "Conflicting claims are flagged rather than averaged"],
                ["6", "Risk and confidence come from what was found"],
                ["7", "The AI writes it up with links to every source"],
                ["8", "The case is saved to your vault, hashed and logged"],
              ].map(([phase, detail]) => (
                <div key={phase} className="flex min-w-0 items-center gap-3 rounded-md border border-panel-border bg-background/40 px-3 py-2">
                  <div className="w-16 shrink-0 font-mono text-[10px] tracking-widest text-cyan sm:w-20">{phase}</div>
                  <div className="min-w-0 flex-1 truncate text-sm">{detail}</div>
                  <Activity className="h-3 w-3 shrink-0 text-success" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 sm:py-24">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <Target className="mx-auto h-10 w-10 text-cyan float-slow" />
          <h2 className="mt-6 font-display text-3xl sm:text-5xl">Check before you<br/> take someone's word for it.</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Your first search takes about a minute to set up. The 1-day trial needs a card to start —
            cancel before it ends and you won't be charged. Whatever you find stays yours.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/pricing" className="group inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 ring-glow">
              Start 1-day free trial <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link to="/get-software" className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-6 py-3 text-sm font-medium hover:bg-panel">
              Talk to us first
            </Link>
          </div>

          <div className="mt-12 flex justify-center">
            <JoinList
              source="cta-section"
              heading="Get the monthly note"
              subheading="What we added, what broke, what we learned. Two minutes to read, once a month."
              cta="Sign me up"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-panel-border py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 sm:px-6">
          <Logo />
          <div className="text-xs text-muted-foreground">
            © 2026 CARIO · Public sources only · Lawful use required
          </div>
          <div className="ml-auto flex gap-5 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-foreground">Refunds</Link>
            <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
          </div>
        </div>
      </footer>
      <SiteChat />
    </div>
  );
}

function Stat({ label, v, tone }: { label: string; v: string; tone?: "amber" | "cyan" }) {
  const c = tone === "amber" ? "text-amber" : tone === "cyan" ? "text-cyan" : "text-foreground";
  return (
    <div>
      <div className={`font-display text-xl font-semibold ${c}`}>{v}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

function QuickScan() {
  const [target, setTarget] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<null | {
    target: string; targetType: string; trustScore: number; confidence: number; recordCount: number; cached?: boolean;
    teaser?: {
      wiki?: { title: string; extract: string; url: string | null } | null;
      wikiAlternates?: string[];
      sanctions?: number;
      newsMentions?: number;
      news?: Array<{ title: string; source: string | null; link: string | null }>;
      sanctionEntities?: Array<{ name: string; datasets: string[]; schema: string | null }>;
    };
  }>(null);

  async function run() {
    if (!target.trim() || busy) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await fetch("/api/public/quick-scan", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ target: target.trim(), email: email.trim() || undefined }),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || `Request failed (${r.status})`);
      }
      const j = await r.json();
      if (j.error) throw new Error(j.error);
      setResult(j);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scan failed");
    } finally {
      setBusy(false);
    }
  }

  const trustTone = !result ? "" : result.trustScore >= 70 ? "text-success" : result.trustScore >= 40 ? "text-amber" : "text-danger";

  return (
    <div className="mt-6 max-w-xl">
      <div className="glass-strong rounded-xl p-3 ring-glow">
        <div className="mb-2 text-xs font-medium text-cyan">Try it free — one search a day, no account needed</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="Enter a name, company, email or domain…"
            className="flex-1 rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none"
          />
          <button
            onClick={run}
            disabled={busy || !target.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
          >
            {busy ? "Searching…" : "Check them"}
          </button>
        </div>
        {error && <div className="mt-2 text-xs text-danger">{error}</div>}

        {result && (
          <div className="mt-3 rounded-md border border-panel-border bg-background/40 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Target · {result.targetType}</div>
                <div className="font-display text-lg">{result.target}</div>
              </div>
              <div className="text-right">
                <div className={`font-display text-3xl ${trustTone}`}>{result.trustScore}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Trust score</div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
              <div><div className="font-display text-base text-foreground">{result.recordCount}</div><div className="text-[10px] text-muted-foreground uppercase tracking-widest">Records</div></div>
              <div><div className="font-display text-base text-foreground">{result.teaser?.sanctions ?? 0}</div><div className="text-[10px] text-muted-foreground uppercase tracking-widest">Sanctions hits</div></div>
              <div><div className="font-display text-base text-foreground">{result.confidence}%</div><div className="text-[10px] text-muted-foreground uppercase tracking-widest">Confidence</div></div>
            </div>
            {result.teaser?.wiki && (
              <div className="mt-2 text-xs text-muted-foreground">
                <span className="text-foreground font-medium">{result.teaser.wiki.title}</span> — {result.teaser.wiki.extract}
              </div>
            )}

            {/* Real free intelligence */}
            {(result.teaser?.sanctionEntities?.length ?? 0) > 0 && (
              <div className="mt-3">
                <div className="text-[11px] font-medium uppercase tracking-widest text-amber">Sanctions and watchlist matches</div>
                <ul className="mt-1.5 space-y-1">
                  {result.teaser!.sanctionEntities!.slice(0, 1).map((s) => (
                    <li key={s.name} className="rounded-md border border-amber/30 bg-amber/5 px-2.5 py-1.5 text-xs">
                      <span className="text-foreground">{s.name}</span>
                      {s.datasets.length > 0 && <span className="text-muted-foreground"> · {s.datasets.join(", ")}</span>}
                    </li>
                  ))}
                  {result.teaser!.sanctionEntities!.slice(1).map((s) => (
                    <li key={s.name} aria-hidden className="select-none rounded-md border border-panel-border bg-background/40 px-2.5 py-1.5 text-xs blur-[5px]">
                      {s.name} · {s.datasets.join(", ") || "restricted dataset"}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(result.teaser?.news?.length ?? 0) > 0 && (
              <div className="mt-3">
                <div className="text-[11px] font-medium uppercase tracking-widest text-cyan">Recent mentions in the press</div>
                <ul className="mt-1.5 space-y-1">
                  {result.teaser!.news!.slice(0, 2).map((n, i) => (
                    <li key={i} className="rounded-md border border-panel-border bg-background/40 px-2.5 py-1.5 text-xs">
                      <span className="text-foreground">{n.title}</span>
                      {n.source && <span className="text-muted-foreground"> · {n.source}</span>}
                    </li>
                  ))}
                  {result.teaser!.news!.slice(2).map((n, i) => (
                    <li key={`b${i}`} aria-hidden className="select-none rounded-md border border-panel-border bg-background/40 px-2.5 py-1.5 text-xs blur-[5px]">
                      <span className="text-foreground">{n.title}</span>
                      {n.source && <span className="text-muted-foreground"> · {n.source}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Locked dossier */}
            <div className="relative mt-3 overflow-hidden rounded-md border border-panel-border bg-background/40 p-3">
              <div aria-hidden className="select-none blur-[6px]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Full dossier</div>
                <div className="mt-2 space-y-1.5 text-xs">
                  {[
                    ["Identity resolution", "4 emails · 9 usernames · 3 wallets clustered"],
                    ["Corporate roles", "2 active directorships · 1 resigned 2023"],
                    ["Breach exposure", "3 credential leaks across 2 datasets"],
                    ["Infrastructure", "6 domains · 2 ASNs · 14 certificates"],
                    ["Contradictions", "1 registry conflict detected"],
                    ["Predictive risk", "Exit-scam probability 12% · 12-month horizon"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-3 rounded border border-panel-border bg-panel/30 px-2 py-1.5">
                      <span className="text-foreground">{k}</span>
                      <span className="text-muted-foreground">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute inset-0 grid place-items-center bg-background/40 p-3 text-center">
                <div>
                  <Lock className="mx-auto h-4 w-4 text-cyan" />
                  <div className="mt-1.5 text-xs text-foreground">
                    {Math.max(0, result.recordCount - 3)} more records — company roles, breaches and linked accounts — are hidden.
                  </div>
                  <Link to="/pricing" className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground ring-glow">
                    Start free trial to unlock <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col sm:flex-row items-stretch gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com — tell me if this changes"
                className="flex-1 rounded-md border border-panel-border bg-background/40 px-3 py-2 text-xs outline-none"
              />
              <button
                onClick={run}
                disabled={busy || !email}
                className="rounded-md border border-cyan/40 bg-cyan/10 px-3 py-2 text-xs font-mono uppercase tracking-widest text-cyan disabled:opacity-60"
              >
                Watch this
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">The rest of the report needs an account.</span>
              <Link to="/signup" className="text-cyan hover:underline">See everything →</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
